// Global configuration loader
// IMPORTANT:
// - In CRA, process.env.REACT_APP_* values are baked at build-time.
// - If public/config.json is missing in an environment, we should NOT fall back to prod URLs.
//   Instead, default to the baked env vars so QA/DEV builds don't accidentally use production.
const env = (typeof process !== "undefined" && process.env) ? process.env : {};

const normalizeBaseUrl = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s : `${s}/`;
};

// ต่อ path ย่อยจาก FILE_URL ของ environment เดียวกัน
// เพื่อไม่ให้ค่าที่ derive ออกมาข้าม environment ไปหาที่อื่น
const deriveFromFileUrl = (fileUrl, subPath) => {
  const base = normalizeBaseUrl(fileUrl);
  if (!base) return "";
  const trimmed = base.replace(/\/+$/, "");
  const suffix = String(subPath || "").replace(/^\/+|\/+$/g, "");
  if (!suffix) return normalizeBaseUrl(trimmed);
  if (new RegExp(`/${suffix}$`, "i").test(trimmed)) return normalizeBaseUrl(trimmed);
  return normalizeBaseUrl(`${trimmed}/${suffix}`);
};

// AUDIT-032/033: เดิมคีย์กลุ่มไฟล์ fallback ไป https://online.ismartsales.com (production)
// ซึ่งขัดกับ comment หัวไฟล์นี้เอง และเป็นค่าที่ถูกใช้จริงเสมอเพราะ public/config.json
// ไม่มีอยู่ในรีโป loadConfig() จึงล้มเหลวแล้วตกมาใช้ defaultConfig ทุกครั้ง
// ผลคือ build ของ QA/DEV ที่ลืมตั้ง env var จะไปอ่าน/เขียนไฟล์บน production เงียบ ๆ
// ตอนนี้ถ้าไม่มีค่า ให้เป็นค่าว่างเพื่อให้พังแบบเห็นได้ ดีกว่าไปโดนของจริง
const defaultConfig = {
  WEB_URL: normalizeBaseUrl(env.REACT_APP_WEB_URL) || "https://localhost:3000/",
  API_URL: normalizeBaseUrl(env.REACT_APP_API_URL) || "https://localhost:8082/api/",
  FILE_URL: normalizeBaseUrl(env.REACT_APP_FILE_URL),
  PHOTO_SALES:
    normalizeBaseUrl(env.REACT_APP_PHOTO_SALES) ||
    deriveFromFileUrl(env.REACT_APP_FILE_URL, "photosales"),
  PHOTO_DOCUMENT:
    normalizeBaseUrl(env.REACT_APP_PHOTO_DOCUMENT) ||
    deriveFromFileUrl(env.REACT_APP_FILE_URL, "form"),
  APP_VERSION: String(env.REACT_APP_VERSION || "1.0.0"),
  APP_ENV: String(env.REACT_APP_ENV || "ISB (DEV)"),
  GRAPH_ENDPOINT_PROFILE: "https://graph.microsoft.com/v1.0/me",
  GRAPH_ENDPOINT_CALENDAR: "https://graph.microsoft.com/v1.0/me/calendars",
};

// ถ้าคีย์ไฟล์ว่าง แปลว่า build นี้ตั้ง env ไม่ครบ ต้องดังพอให้เห็นตั้งแต่เปิดหน้าแรก
const REQUIRED_URL_KEYS = ["FILE_URL", "PHOTO_SALES", "PHOTO_DOCUMENT"];

const warnMissingUrls = (source) => {
  const missing = REQUIRED_URL_KEYS.filter((key) => !config[key]);
  if (missing.length > 0) {
    console.error(
      `[config] ไม่พบค่า ${missing.join(", ")} (${source}) — ` +
        "การอ่าน/อัปโหลดไฟล์จะไม่ทำงาน กรุณาตั้ง REACT_APP_* ตอน build หรือใส่ public/config.json",
    );
  }
};

// Store config in module scope
let config = { ...defaultConfig };
let configPromise = null;
let configLoaded = false;

/**
 * Load configuration from public/config.json
 * This will cache the result
 */
export const loadConfig = async () => {
  if (configLoaded) {
    return config;
  }

  if (configPromise) {
    return configPromise;
  }

  const publicUrl = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL)
    ? String(process.env.PUBLIC_URL).replace(/\/$/, '')
    : '';

  /*
   * ต้องกัน cache ทุกชั้น — ไฟล์นี้มีไว้เพื่อ "แก้ URL โดยไม่ต้อง build ใหม่"
   * ถ้าเบราว์เซอร์ยังใช้ของเก่าก็เสียจุดประสงค์ทั้งหมด
   *
   * เจอจริงบน CLEAN: แก้ค่าใน config.json บน server แล้ว แต่ IIS ตอบ ETag เดิม
   * (`W/"...:0"`) ⇒ เบราว์เซอร์ส่ง If-None-Match แล้วได้ 304 กลับมา จึงใช้ body
   * ที่ cache ไว้ต่อ ยิงไป URL เก่าที่ไม่มีอยู่แล้ว ขึ้นเป็น CORS error ที่ชวนหลงทาง
   *
   * ใส่ทั้ง query ที่ไม่ซ้ำ (กัน proxy/CDN) และ cache:'no-store' (กันเบราว์เซอร์)
   */
  const bust = `?v=${Date.now()}`;
  configPromise = fetch(`${publicUrl}/config.json${bust}`, { cache: 'no-store' })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      config = { ...defaultConfig, ...data };
      configLoaded = true;
      warnMissingUrls('config.json');
      return config;
    })
    .catch(error => {
      console.warn('Failed to load config.json, using default values:', error);
      config = defaultConfig;
      configLoaded = true;
      warnMissingUrls('ค่าจาก env ตอน build');
      return config;
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
};

/**
 * Get config value by key
 * @param {string} key - Config key to get
 * @param {any} defaultValue - Default value if key not found
 * @returns {any} Config value
 */
export const getConfig = (key, defaultValue = null) => {
  return config?.[key] ?? defaultValue;
};

/**
 * Get all config
 * @returns {object} All config values
 */
export const getAllConfig = () => {
  return { ...config };
};

/**
 * Check if config is loaded
 * @returns {boolean}
 */
export const isConfigLoaded = () => {
  return configLoaded;
};

/**
 * Clear config cache (useful for testing)
 */
export const clearConfigCache = () => {
  config = { ...defaultConfig };
  configPromise = null;
  configLoaded = false;
};

const configExport = {
  loadConfig,
  getConfig,
  getAllConfig,
  isConfigLoaded,
  clearConfigCache
};

export default configExport;
